import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { onSnapshot } from "firebase/firestore";
import { propertyCollection } from "../utils/firestorePaths";
import { useNavigate } from "react-router-dom";
import { calculateDaysRemaining } from "../utils/warrantyUtils";

const WarrantyWidget = ({ clientId, selectedProperty }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({
    totalActive: 0,
    expiringSoon: [],
    recentlyExpired: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedProperty) {
      setLoading(false);
      return;
    }

    const warrantiesRef = propertyCollection(
      clientId,
      selectedProperty,
      "warranties"
    );

    const unsubscribe = onSnapshot(
      warrantiesRef,
      (snapshot) => {
        const warranties = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          daysRemaining: calculateDaysRemaining(doc.data().warrantyEndDate),
        }));

        const active = warranties.filter((w) => w.daysRemaining >= 0);
        const expiring = warranties
          .filter((w) => w.daysRemaining >= 0 && w.daysRemaining <= 30)
          .sort((a, b) => a.daysRemaining - b.daysRemaining); // Sort by most urgent first
        const expired = warranties.filter((w) => w.daysRemaining < 0);

        setSummary({
          totalActive: active.length,
          expiringSoon: expiring.slice(0, 5), // Show only top 5
          recentlyExpired: expired.length,
        });
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching warranty summary:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [selectedProperty]);

  const handleViewAll = () => {
    navigate("/warranty-tracker");
  };

  const handleViewExpiring = () => {
    navigate("/warranty-tracker?filter=expiring");
  };

  if (loading) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" component="div">
            Warranty Tracker
          </Typography>
          <CheckCircleIcon color="primary" />
        </Box>

        {/* Summary Statistics */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1, textAlign: "center", p: 1, bgcolor: "success.lighter", borderRadius: 1 }}>
            <Typography variant="h5" color="success.main" fontWeight={600}>
              {summary.totalActive}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Active
            </Typography>
          </Box>

          <Box sx={{ flex: 1, textAlign: "center", p: 1, bgcolor: "warning.lighter", borderRadius: 1 }}>
            <Typography variant="h5" color="warning.main" fontWeight={600}>
              {summary.expiringSoon.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Expiring Soon
            </Typography>
          </Box>

          <Box sx={{ flex: 1, textAlign: "center", p: 1, bgcolor: "error.lighter", borderRadius: 1 }}>
            <Typography variant="h5" color="error.main" fontWeight={600}>
              {summary.recentlyExpired}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Expired
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Expiring Warranties List */}
        {summary.expiringSoon.length > 0 ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <WarningIcon color="warning" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>
                Expiring in Next 30 Days
              </Typography>
            </Box>

            <List dense sx={{ bgcolor: "background.paper", maxHeight: 200, overflow: "auto" }}>
              {summary.expiringSoon.map((w) => (
                <ListItem
                  key={w.id}
                  sx={{
                    borderLeft: 3,
                    borderColor: w.daysRemaining <= 7 ? "error.main" : "warning.main",
                    mb: 0.5,
                    bgcolor: w.daysRemaining <= 7 ? "error.lighter" : "warning.lighter",
                    borderRadius: 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}>
                        {w.itemName} - {w.serialNumber}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                        <Chip
                          label={`${w.daysRemaining} day${w.daysRemaining !== 1 ? "s" : ""}`}
                          size="small"
                          color={w.daysRemaining <= 7 ? "error" : "warning"}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Expires: {w.warrantyEndDate}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {summary.expiringSoon.length > 5 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Showing 5 of {summary.expiringSoon.length} expiring warranties
              </Typography>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: "success.main", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No warranties expiring in the next 30 days
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleViewAll}
            fullWidth
          >
            View All
          </Button>
          {summary.expiringSoon.length > 0 && (
            <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={handleViewExpiring}
              fullWidth
            >
              View Expiring
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default WarrantyWidget;
